import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SearchIcon from '@mui/icons-material/Search';
import { format, parseISO } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { useLanguage } from '../contexts/LanguageContext';

interface AssignedDevice {
  id: number;
  inventoryNumber: string;
  type: string;
  model: string;
  serialNumber: string | null;
  issueDate: string;
  expectedReturnDate: string | null;
}

interface EmployeeGroup {
  id: number;
  fullName: string;
  position: string | null;
  organization: { id: number; name: string } | null;
  devices: AssignedDevice[];
}

interface InStockDevice {
  id: number;
  inventoryNumber: string;
  type: string;
  model: string;
  serialNumber: string | null;
  condition: string | null;
  organization: { id: number; name: string } | null;
}

interface ReportData {
  generatedAt: string;
  totals: {
    employeesWithDevices: number;
    assignedDevices: number;
    issuedDevices: number;
    inStockDevices: number;
    writtenOffDevices: number;
  };
  employees: EmployeeGroup[];
  inStock: {
    total: number;
    byType: { type: string; count: number }[];
    devices: InStockDevice[];
  };
}

export default function ReportPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = useMemo(() => (language === 'ru' ? ru : enUS), [language]);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<ReportData>('/reports/summary')
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setError('');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || t('reports.errorLoading'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const employees = data?.employees ?? [];
  const inStockDevices = data?.inStock.devices ?? [];

  // Organizations that appear in the report (staffed employees and in-stock owners).
  const organizations = useMemo(() => {
    const map = new Map<number, string>();
    for (const employee of employees) {
      if (employee.organization) map.set(employee.organization.id, employee.organization.name);
    }
    for (const device of inStockDevices) {
      if (device.organization) map.set(device.organization.id, device.organization.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, inStockDevices]);

  const selectedOrgId = orgFilter === 'all' ? null : Number(orgFilter);

  const filteredEmployees = useMemo<EmployeeGroup[]>(() => {
    const q = search.trim().toLowerCase();
    const result: EmployeeGroup[] = [];
    for (const employee of employees) {
      if (selectedOrgId !== null && employee.organization?.id !== selectedOrgId) continue;
      if (!q) {
        result.push(employee);
        continue;
      }

      const employeeMatches =
        employee.fullName.toLowerCase().includes(q) ||
        (employee.position?.toLowerCase().includes(q) ?? false) ||
        (employee.organization?.name.toLowerCase().includes(q) ?? false);

      const devices = employee.devices.filter(
        (d) =>
          d.inventoryNumber.toLowerCase().includes(q) ||
          d.model.toLowerCase().includes(q) ||
          d.type.toLowerCase().includes(q) ||
          (d.serialNumber?.toLowerCase().includes(q) ?? false)
      );

      if (employeeMatches) result.push(employee);
      else if (devices.length > 0) result.push({ ...employee, devices });
    }
    return result;
  }, [employees, search, selectedOrgId]);

  const filteredInStock = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inStockDevices.filter((d) => {
      if (selectedOrgId !== null && d.organization?.id !== selectedOrgId) return false;
      if (typeFilter !== 'all' && d.type !== typeFilter) return false;
      if (!q) return true;
      return (
        d.inventoryNumber.toLowerCase().includes(q) ||
        d.model.toLowerCase().includes(q) ||
        d.type.toLowerCase().includes(q) ||
        (d.serialNumber?.toLowerCase().includes(q) ?? false) ||
        (d.organization?.name.toLowerCase().includes(q) ?? false)
      );
    });
  }, [inStockDevices, search, typeFilter, selectedOrgId]);

  const statCards = useMemo(() => {
    const totals = data?.totals;
    return [
      {
        key: 'employees',
        label: t('reports.statEmployees'),
        value: totals?.employeesWithDevices ?? 0,
        icon: <GroupsIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #8b8bff 0%, #5b5bf6 100%)'
          : 'linear-gradient(135deg, #5b5bf6 0%, #8b8bff 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(139,139,255,0.6)'
          : '0 14px 36px -14px rgba(91,91,246,0.45)',
      },
      {
        key: 'issued',
        label: t('reports.statAssigned'),
        value: totals?.assignedDevices ?? 0,
        icon: <AssignmentTurnedInIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #f59e0b 0%, #f472b6 100%)'
          : 'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(245,158,11,0.55)'
          : '0 14px 36px -14px rgba(245,158,11,0.4)',
      },
      {
        key: 'inStock',
        label: t('reports.statInStock'),
        value: totals?.inStockDevices ?? 0,
        icon: <InventoryIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #34d399 0%, #0ea5a5 100%)'
          : 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(52,211,153,0.55)'
          : '0 14px 36px -14px rgba(16,185,129,0.4)',
      },
      {
        key: 'writtenOff',
        label: t('reports.statWrittenOff'),
        value: totals?.writtenOffDevices ?? 0,
        icon: <DeleteSweepIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(248,113,113,0.55)'
          : '0 14px 36px -14px rgba(239,68,68,0.4)',
      },
    ];
  }, [data, isDark, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ borderRadius: 1.5 }}>
        {error || t('reports.errorLoading')}
      </Alert>
    );
  }

  const sectionPaperSx = {
    borderRadius: 1.5,
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    overflow: 'hidden',
  } as const;

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #8b8bff 20%, #5ee7f1 80%)'
                : 'linear-gradient(135deg, #5b5bf6 20%, #22c3d1 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(139,139,255,0.7)'
                : '0 8px 20px -8px rgba(91,91,246,0.5)',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('reports.intro')} · {t('reports.generatedAt')}:{' '}
            <strong style={{ color: 'var(--text)' }}>
              {format(parseISO(data.generatedAt), 'dd.MM.yyyy HH:mm', { locale })}
            </strong>
          </Typography>
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {statCards.map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.key}>
            <Card>
              <CardContent sx={{ p: 3, display: 'flex', gap: 2.5, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: s.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    boxShadow: s.shadow,
                    flexShrink: 0,
                    '& svg': { fontSize: 28 },
                  }}
                >
                  {s.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-muted)', mt: 0.5 }}>
                    {s.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filter bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1.5,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: '1 1 300px' }}
            placeholder={t('reports.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('common.organization')}</InputLabel>
            <Select
              value={orgFilter}
              label={t('common.organization')}
              onChange={(e) => setOrgFilter(e.target.value)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={String(org.id)}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('reports.typeFilter')}</InputLabel>
            <Select
              value={typeFilter}
              label={t('reports.typeFilter')}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              {data.inStock.byType.map((item) => (
                <MenuItem key={item.type} value={item.type}>
                  {item.type} ({item.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(search || typeFilter !== 'all' || orgFilter !== 'all') && (
            <>
              <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                {filteredEmployees.length + filteredInStock.length} {t('common.records')}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('all');
                  setOrgFilter('all');
                }}
              >
                {t('devices.clearFilters')}
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Assigned equipment by employee */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('reports.assignedTitle')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
            {t('reports.assignedDesc')}
          </Typography>
          <Chip
            size="small"
            label={`${filteredEmployees.length} ${t('reports.employeesSuffix')} · ${filteredEmployees.reduce((s, e) => s + e.devices.length, 0)} ${t('reports.units')}`}
            sx={{ ml: 'auto', fontWeight: 600 }}
          />
        </Box>
        <Paper sx={sectionPaperSx}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.tableInvNumber')}</TableCell>
                  <TableCell>{t('reports.tableModel')}</TableCell>
                  <TableCell>{t('reports.tableSerial')}</TableCell>
                  <TableCell>{t('reports.tableIssueDate')}</TableCell>
                  <TableCell>{t('reports.tableExpectedReturn')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                      {t('reports.noEmployees')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => (
                    <React.Fragment key={employee.id}>
                      <TableRow
                        sx={{
                          '& td': {
                            py: 1.25,
                            background: isDark
                              ? 'linear-gradient(90deg, rgba(139,139,255,0.12), rgba(94,231,241,0.04))'
                              : 'linear-gradient(90deg, rgba(91,91,246,0.08), rgba(34,195,209,0.03))',
                          },
                        }}
                      >
                        <TableCell colSpan={5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {employee.fullName}
                            </Typography>
                            {employee.position && (
                              <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                                {employee.position}
                              </Typography>
                            )}
                            {employee.organization && (
                              <Chip
                                size="small"
                                label={employee.organization.name}
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.68rem' }}
                              />
                            )}
                            <Chip
                              size="small"
                              label={`${employee.devices.length} ${t('reports.units')}`}
                              sx={{ ml: 'auto', height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      {employee.devices.map((device) => (
                        <TableRow key={device.id}>
                          <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                            {device.inventoryNumber}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {device.model}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block' }}>
                              {device.type}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                            {device.serialNumber || '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                            {format(parseISO(device.issueDate), 'dd.MM.yyyy', { locale })}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                            {device.expectedReturnDate
                              ? format(parseISO(device.expectedReturnDate), 'dd.MM.yyyy', { locale })
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Stock ready to issue */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('reports.inStockTitle')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
            {t('reports.inStockDesc')}
          </Typography>
          <Chip
            size="small"
            label={`${filteredInStock.length} ${t('reports.units')}`}
            sx={{ ml: 'auto', fontWeight: 600 }}
          />
        </Box>

        {/* Type breakdown */}
        {data.inStock.byType.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {data.inStock.byType.map((item) => (
              <Chip
                key={item.type}
                label={`${item.type}: ${item.count}`}
                variant={typeFilter === item.type ? 'filled' : 'outlined'}
                color={typeFilter === item.type ? 'primary' : 'default'}
                onClick={() => setTypeFilter(typeFilter === item.type ? 'all' : item.type)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
        )}

        <Paper sx={sectionPaperSx}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.tableInvNumber')}</TableCell>
                  <TableCell>{t('reports.tableModel')}</TableCell>
                  <TableCell>{t('reports.tableSerial')}</TableCell>
                  <TableCell>{t('reports.tableCondition')}</TableCell>
                  <TableCell>{t('reports.tableOrganization')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInStock.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                      {t('reports.noInStock')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInStock.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                        {device.inventoryNumber}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {device.model}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block' }}>
                          {device.type}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                        {device.serialNumber || '—'}
                      </TableCell>
                      <TableCell>
                        {device.condition ? (
                          <Chip
                            size="small"
                            label={device.condition}
                            sx={{
                              fontWeight: 600,
                              background: isDark ? 'rgba(52,211,153,0.15)' : 'rgba(16,185,129,0.12)',
                              border: 'none',
                            }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-muted)' }}>
                        {device.organization?.name || '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}
